import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  BsCameraVideoFill, BsCameraVideoOffFill, BsGripVertical, BsDashLg,
  BsArrowsAngleExpand, BsMicMuteFill,
} from 'react-icons/bs';
import { createUserAvatarDataUri } from '../utils/avatarUtils';

function isMobile() {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;
}

/**
 * One stacked tile — video if available, otherwise an avatar/initial placeholder.
 * Read-only mic-status badge (Zoom-style): shows a red mic-off badge ONLY when
 * the participant has their own mic muted; nothing when they're unmuted.
 * Green ring when the participant is speaking.
 */
function VideoTile({
  track, label, isLocal, speaking, seed, micMuted, size,
}) {
  const ref = useRef(null);
  // Avatar is regenerated locally from the participant's stable identity, never
  // taken from remote metadata — see createUserAvatarDataUri.
  const avatarUri = useMemo(() => createUserAvatarDataUri(seed || label), [seed, label]);

  useEffect(() => {
    const el = ref.current;
    if (!track || !el) return;
    try { track.attach(el); } catch (e) {}
    el.play && el.play().catch(() => {});

    // Stale-frame recovery: a subscribed, unmuted video track can stop
    // delivering frames (dynacast layer paused, subscription churn during a
    // reconnect) while staying "attached" — the tile just goes black with no
    // recovery. Watch decoded frames; if none arrive for a while, re-attach
    // the track to kick the pipeline.
    let lastFrameAt = Date.now();
    let rafId = null;
    let cancelled = false;
    const hasRVFC = typeof el.requestVideoFrameCallback === 'function';
    if (hasRVFC) {
      const onFrame = () => {
        if (cancelled) return;
        lastFrameAt = Date.now();
        rafId = el.requestVideoFrameCallback(onFrame);
      };
      rafId = el.requestVideoFrameCallback(onFrame);
    }
    const STALE_MS = 4000;
    const watchdog = setInterval(() => {
      // Backgrounded tab pauses rVFC — don't treat that as a freeze.
      if (cancelled || document.hidden) { lastFrameAt = Date.now(); return; }
      // Only act when frames *should* be flowing — skip if the source is muted.
      const srcMuted = !!(track.mediaStreamTrack && track.mediaStreamTrack.muted);
      if (srcMuted) { lastFrameAt = Date.now(); return; }
      const stale = hasRVFC
        ? (Date.now() - lastFrameAt > STALE_MS)
        : (el.readyState < 2 || el.videoWidth === 0);
      if (stale) {
        try { track.detach(el); } catch (e) {}
        try { track.attach(el); } catch (e) {}
        el.play && el.play().catch(() => {});
        lastFrameAt = Date.now(); // give the re-attach a fresh window
      }
    }, 2000);

    return () => {
      cancelled = true;
      clearInterval(watchdog);
      if (hasRVFC && rafId != null && typeof el.cancelVideoFrameCallback === 'function') {
        try { el.cancelVideoFrameCallback(rafId); } catch (e) {}
      }
      try { track.detach(el); } catch (e) {}
      if (el) el.srcObject = null;
    };
  }, [track]);

  const initial = (label || '?').trim().charAt(0).toUpperCase();
  const showVideo = !!track;

  return (
    <div
      style={{
        width: `${size.w}px`,
        height: `${size.h}px`,
        backgroundColor: '#000',
        borderRadius: '8px',
        overflow: 'hidden',
        position: 'relative',
        flexShrink: 0,
        boxShadow: speaking
          ? '0 0 0 2px #30d158, 0 2px 8px rgba(0,0,0,0.32)'
          : '0 2px 6px rgba(0,0,0,0.25)',
        transition: 'box-shadow 0.18s ease',
      }}
    >
      {/* Video element (attached only when track exists) */}
      <video
        ref={ref}
        autoPlay
        playsInline
        muted={isLocal}
        style={{
          width: '100%', height: '100%', objectFit: 'cover',
          display: showVideo ? 'block' : 'none',
          transform: isLocal ? 'scaleX(-1)' : undefined,
        }}
      />

      {/* Placeholder when no video */}
      {!showVideo && (
        <div
          style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(180deg, #1c1c1e 0%, #0c0c0d 100%)',
          }}
        >
          {avatarUri ? (
            <img
              src={avatarUri}
              alt={label}
              style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }}
            />
          ) : (
            <div
              style={{
                width: 48, height: 48, borderRadius: '50%',
                background: 'rgba(255,255,255,0.10)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#f5f5f7', fontSize: 18, fontWeight: 600,
              }}
            >
              {initial}
            </div>
          )}
        </div>
      )}

      {/* Bottom-left: name pill — the mic-muted status lives inline here as a
          small icon (Zoom-style), rather than a separate large badge. */}
      <div
        style={{
          position: 'absolute',
          bottom: 6,
          left: 6,
          maxWidth: '88%',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          color: '#fff',
          fontSize: 11,
          fontWeight: 500,
          textShadow: '0 1px 2px rgba(0,0,0,0.7)',
          background: 'rgba(0,0,0,0.38)',
          padding: '2px 6px',
          borderRadius: 5,
        }}
      >
        {micMuted && (
          <BsMicMuteFill
            size={10}
            style={{ color: '#ff5e54', flexShrink: 0 }}
            aria-label={isLocal ? 'Your microphone is muted' : `${label} is muted`}
          />
        )}
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
      </div>
    </div>
  );
}

/**
 * VideoTilesUI
 *   - Always shows at least the local "You" tile (placeholder when video off).
 *   - One column, tiles stacked vertically. Scrolls when there are many.
 *   - Mic toggle on each tile (Zoom-style); green ring when speaking.
 *   - Draggable via the grip handle; collapse button hides the tiles.
 *
 * Props:
 *   sdk            StreamPixelVoiceChat
 *   participants   { localParticipant, remoteParticipants } — each carries
 *                  `micMuted` (the participant's own mic state, read-only here)
 *   darkMode       bool — follows the project's chatTheme
 */
export default function VideoTilesUI({
  sdk,
  participants,
  darkMode = true,
  position = 'Right',   // chat panel side; tiles sit on the OPPOSITE side
}) {
  // Theme palette — mirrors the dark/light split used by ChatPanel & toasts.
  const theme = darkMode
    ? {
        panelBg: 'rgba(20, 20, 25, 0.78)',
        border: '0.5px solid rgba(255,255,255,0.08)',
        shadow: '0 8px 28px rgba(0,0,0,0.45)',
        barBg: 'rgba(255,255,255,0.04)',
        toggleBarBg: 'rgba(255,255,255,0.03)',
        text: 'rgba(235,235,245,0.7)',
        textDim: 'rgba(235,235,245,0.55)',
      }
    : {
        panelBg: 'rgba(252, 252, 253, 0.86)',
        border: '0.5px solid rgba(0,0,0,0.07)',
        shadow: '0 8px 28px rgba(0,0,0,0.18)',
        barBg: 'rgba(0,0,0,0.04)',
        toggleBarBg: 'rgba(0,0,0,0.03)',
        text: 'rgba(60,60,67,0.75)',
        textDim: 'rgba(60,60,67,0.5)',
      };
  // Single source of truth for the camera-toggle button: derive from the live
  // participant state, so a failed setCameraEnabled (permission denied, no
  // camera, in use) doesn't leave the button stuck on "on" while the real
  // publication is off. Same pattern the mic uses via toggleMicChat ←
  // onMicStateChange.
  const camOn = !!participants?.localParticipant?.videoTrack;
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [pos, setPos] = useState(() => {
    const m = isMobile() ? 8 : 16;
    // Anchor tiles on the side OPPOSITE the chat panel so they don't overlap:
    // chat on the Left → tiles on the Right; chat on the Right → tiles on the Left.
    return position === 'Left' ? { right: m, top: m } : { left: m, top: m };
  });

  const dragRef = useRef(null);
  const panelRef = useRef(null);

  const TILE = isMobile() ? { w: 110, h: 82 } : { w: 150, h: 112 };
  const PANEL_WIDTH = TILE.w + 16;  // tile + horizontal padding
  const MAX_PANEL_HEIGHT = Math.min(
    typeof window !== 'undefined' ? window.innerHeight - 48 : 600,
    isMobile() ? 320 : 560,
  );

  const handleToggleCam = useCallback(async (e) => {
    e?.stopPropagation?.();
    if (!sdk || busy) return;
    setBusy(true);
    try {
      // camOn is derived from the live participant state — no need to
      // optimistically update it here. _emitParticipants() fires inside
      // toggleVideo and reconciles the button to reality.
      await sdk.toggleVideo(!camOn);
    } catch (err) {
      console.error('toggleVideo failed:', err);
    } finally {
      setBusy(false);
    }
  }, [sdk, camOn, busy]);

  const startDrag = useCallback((clientX, clientY, panelEl) => {
    const rect = panelEl.getBoundingClientRect();
    dragRef.current = { offX: clientX - rect.left, offY: clientY - rect.top, pw: rect.width, ph: rect.height };
    setPos({ left: rect.left, top: rect.top });
  }, []);
  const onDragMove = useCallback((clientX, clientY) => {
    const d = dragRef.current;
    if (!d) return;
    const x = Math.max(0, Math.min(window.innerWidth - d.pw, clientX - d.offX));
    const y = Math.max(0, Math.min(window.innerHeight - d.ph, clientY - d.offY));
    setPos({ left: x, top: y });
  }, []);
  const endDrag = useCallback(() => { dragRef.current = null; }, []);

  useEffect(() => {
    const onMouseMove = (e) => { if (dragRef.current) onDragMove(e.clientX, e.clientY); };
    const onMouseUp = () => endDrag();
    const onTouchMove = (e) => {
      if (dragRef.current && e.touches[0]) onDragMove(e.touches[0].clientX, e.touches[0].clientY);
    };
    const onTouchEnd = () => endDrag();
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [onDragMove, endDrag]);

  const onHeaderMouseDown  = (e) => { if (panelRef.current) startDrag(e.clientX, e.clientY, panelRef.current); };
  const onHeaderTouchStart = (e) => {
    if (panelRef.current && e.touches[0]) startDrag(e.touches[0].clientX, e.touches[0].clientY, panelRef.current);
  };

  const local = participants?.localParticipant;
  const remotes = participants?.remoteParticipants || [];

  // One tile per participant in the room — local user plus every remote,
  // regardless of whether their camera is on. Camera-off participants render
  // the avatar/initial placeholder (VideoTile handles a null track).
  const tiles = [
    {
      key: 'local',
      track: local?.videoTrack,
      label: 'You',
      isLocal: true,
      speaking: !!local?.speaking,
      seed: local?.id || 'You',
      micMuted: !!local?.micMuted,
    },
    ...remotes.map(p => ({
      key: p.id,
      track: p.videoTrack,           // null when their camera is off → placeholder
      label: (p.id || '').split('&')[0],
      isLocal: false,
      speaking: !!p.speaking,
      seed: p.id,
      micMuted: !!p.micMuted,
    })),
  ];

  const positionStyle = pos.left !== undefined
    ? { left: `${pos.left}px`, top: `${pos.top}px` }
    : { right: `${pos.right}px`, top: `${pos.top}px` };

  return (
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        zIndex: 200002,
        ...positionStyle,
        display: 'flex',
        flexDirection: 'column',
        background: theme.panelBg,
        backdropFilter: 'saturate(160%) blur(20px)',
        WebkitBackdropFilter: 'saturate(160%) blur(20px)',
        borderRadius: 12,
        boxShadow: theme.shadow,
        border: theme.border,
        overflow: 'hidden',
        width: `${PANEL_WIDTH}px`,
        maxHeight: `${MAX_PANEL_HEIGHT}px`,
        userSelect: 'none',
      }}
    >
      {/* Header: drag handle + count + collapse */}
      <div
        onMouseDown={onHeaderMouseDown}
        onTouchStart={onHeaderTouchStart}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 8px',
          cursor: 'grab',
          background: theme.barBg,
          touchAction: 'none',
          flexShrink: 0,
        }}
      >
        <BsGripVertical size={13} color={theme.textDim} />
        <span style={{ color: theme.text, fontSize: 10, fontWeight: 500, letterSpacing: 0.2 }}>
          {tiles.length} {tiles.length === 1 ? 'tile' : 'tiles'}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); setCollapsed(c => !c); }}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          style={{
            background: 'transparent', border: 'none',
            color: theme.text, cursor: 'pointer',
            padding: 2, display: 'flex', alignItems: 'center',
          }}
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? <BsArrowsAngleExpand size={11} /> : <BsDashLg size={11} />}
        </button>
      </div>

      {/* Tile column — scrolls when there are many participants */}
      {!collapsed && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            padding: 8,
            overflowY: 'auto',
            overflowX: 'hidden',
            minHeight: 0,
            flex: '1 1 auto',
          }}
        >
          {tiles.map(t => (
            <VideoTile
              key={t.key}
              track={t.track}
              label={t.label}
              isLocal={t.isLocal}
              speaking={t.speaking}
              seed={t.seed}
              micMuted={t.micMuted}
              size={TILE}
            />
          ))}
        </div>
      )}

      {/* Camera toggle bar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          padding: 6,
          background: theme.toggleBarBg,
          flexShrink: 0,
        }}
      >
        <button
          onClick={handleToggleCam}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          disabled={busy}
          title={camOn ? 'Turn camera off' : 'Turn camera on'}
          style={{
            width: 32, height: 32, borderRadius: '50%', border: 'none',
            background: camOn ? '#ff453a' : '#0a84ff',
            color: '#fff', cursor: busy ? 'wait' : 'pointer',
            opacity: busy ? 0.6 : 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {camOn ? <BsCameraVideoOffFill size={14} /> : <BsCameraVideoFill size={14} />}
        </button>
      </div>
    </div>
  );
}
